import {
  Button,
  ButtonGroup,
  H4,
  H5,
  Input,
  Message,
  Stack,
  P
} from "@adfenix/alexandria";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ThinMessageWrapper } from "./common/helper";
import AuthPage from "components/AuthPage";
import styled from "styled-components";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";

import { setPasswordReturnUrl } from "api/setPasswordHelper";

import { resendEmail } from "api/services/authService";

const CenteredHeading = styled(H4)`
  text-align: center;
`;

const StyledButton = styled(Button)`
  margin-bottom: 10px !important;
`;

const StyledMessage = styled(ThinMessageWrapper)`
  ol,
  ul {
    padding-left: 16px;
  }

  ul {
    list-style-type: disc;
  }
`;

const SetupAccount = () => {
  const [searchParams] = useSearchParams();
  let returnUrl = searchParams.get("returnUrl");
  if (returnUrl) returnUrl = decodeURIComponent(returnUrl);

  const email = decodeURIComponent(searchParams.get("email") ?? "");
  const [instructionsSent, setInstructionsSent] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { t } = useTranslation();
  const navigate = useNavigate();

  const { mutateAsync: triggerResendEmail, isLoading: isSending } = useMutation(
    resendEmail,
    {
      onError: () => {
        setError(
          "Something went wrong while sending the instructions, please try again or contact support at support@adfenix.com"
        );
      }
    }
  );

  const sendInstructions = async () => {
    if (returnUrl) setPasswordReturnUrl(returnUrl);

    await triggerResendEmail({ email });

    setInstructionsSent(true);
  };

  if (instructionsSent) {
    return (
      <AuthPage>
        <Stack spacing="l">
          <H4 style={{ textAlign: "center" }}>
            {t("login_sent_instructions_header")}
          </H4>

          <StyledMessage>
            <P>{t("login_sent_instructions_email")}</P>
          </StyledMessage>

          <StyledMessage>
            <P>
              <ol>
                <li>{t("login_sent_instructions_email_step1")}</li>
                <li>{t("login_sent_instructions_email_step2")}</li>
                <li>{t("login_sent_instructions_email_step3")}</li>
                <li>{t("login_sent_instructions_email_step4")}</li>
              </ol>
            </P>
            <hr />
          </StyledMessage>

          <H4 style={{ textAlign: "center" }}>
            {t("login_sent_instructions_no_email_header")}
          </H4>

          <StyledMessage>
            <P>
              <ul>
                <li>{t("login_sent_instructions_no_email_bullet1")}</li>
                <li>{t("login_sent_instructions_no_email_bullet2")}</li>
              </ul>
            </P>
          </StyledMessage>

          <StyledMessage>
            <P>
              {t("login_sent_instructions_no_email_body")}{" "}
              <a
                href="mailto:support@adfenix.com"
                data-loglabel="email-support-adfenix-link"
              >
                support@adfenix.com
              </a>
            </P>
          </StyledMessage>

          <ButtonGroup alignItems="stretch" flexDirection="column">
            <StyledButton
              variant="primary"
              onClick={sendInstructions}
              isLoading={isSending}
              logLabel="resend-instructions-button"
            >
              {t("login_send_instructions")}
            </StyledButton>
          </ButtonGroup>
        </Stack>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <Stack spacing="xl2">
        <Stack spacing="m">
          {error && (
            <ThinMessageWrapper>
              <Message type="error">{error}</Message>
            </ThinMessageWrapper>
          )}

          <CenteredHeading>{t("login_setup_account_title")}</CenteredHeading>

          <Input
            label={t("login.cognito.card.email.label")}
            autoFocus
            type="email"
            readOnly={true}
            value={email}
            placeholder={t("login.cognito.card.email.placeholder")}
          />
        </Stack>

        <ThinMessageWrapper>
          <H5>{t("login_no_password_header")}</H5>
          <P>
            {t("login_no_password_body", {
              email
            })}
          </P>
        </ThinMessageWrapper>

        <ButtonGroup alignItems="stretch" flexDirection="column">
          <StyledButton
            variant="primary"
            logLabel="send-instructions-button"
            onClick={sendInstructions}
            isLoading={isSending}
          >
            {t("login_send_instructions")}
          </StyledButton>

          <StyledButton
            variant="secondary"
            logLabel="setup-account-back-button"
            onClick={() => {
              navigate("/login?email=" + encodeURIComponent(email));
            }}
          >
            {t("order.footer.backButton")}
          </StyledButton>
        </ButtonGroup>
      </Stack>
    </AuthPage>
  );
};

export default SetupAccount;
